import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, AlertCircle, ArrowLeft, Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentResult = ({ sessionId, onDone }) => {
  const [status, setStatus] = useState("checking");
  const [booking] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("easyskip_pending_booking") || "null");
    } catch {
      return null;
    }
  });

  useEffect(() => {
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          sessionStorage.removeItem("easyskip_pending_booking");
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts >= 15) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-5 py-20">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-lg rounded-2xl border border-ink/10 bg-white p-8 text-center shadow-[0_24px_60px_rgba(0,0,0,0.08)] sm:p-10"
        data-testid="payment-result"
      >
        {status === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-emerald-700" />
            <h1 className="mt-6 font-heading text-2xl font-extrabold text-ink sm:text-3xl">
              Confirming your payment…
            </h1>
            <p className="mt-3 text-sm text-ink/60">One moment while we check with Stripe.</p>
          </div>
        )}

        {status === "paid" && (
          <div data-testid="payment-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-700" />
            <h1 className="mt-6 font-heading text-2xl font-extrabold text-ink sm:text-3xl">
              Payment successful
            </h1>
            {booking && (
              <>
                <p className="mt-2 font-mono text-sm tracking-[0.2em] text-emerald-700" data-testid="payment-reference">
                  REF · {booking.reference}
                </p>
                <div className="mx-auto mt-7 max-w-sm space-y-3 rounded-xl border border-ink/10 bg-black/[0.02] p-6 text-left text-sm">
                  <p className="flex justify-between gap-4">
                    <span className="text-ink/55">Skip</span>
                    <span className="font-semibold text-ink">{booking.skip}</span>
                  </p>
                  <p className="flex justify-between gap-4">
                    <span className="text-ink/55">Delivery date</span>
                    <span className="font-semibold text-ink">{booking.date}</span>
                  </p>
                  <p className="flex justify-between gap-4">
                    <span className="text-ink/55">Paid</span>
                    <span className="font-semibold text-emerald-700">
                      {booking.price ? `€${booking.price} — card` : "Card payment"}
                    </span>
                  </p>
                </div>
                <p className="mx-auto mt-6 max-w-sm text-sm leading-relaxed text-ink/65">
                  Your receipt is on its way to{" "}
                  <span className="font-semibold text-ink">{booking.email}</span>. We'll call{" "}
                  <span className="font-semibold text-ink">{booking.phone}</span> shortly to
                  confirm your delivery slot.
                </p>
              </>
            )}
            <button
              data-testid="payment-back-button"
              onClick={onDone}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 font-heading text-sm font-bold text-white transition-colors duration-300 hover:bg-emerald-800"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Easyskip
            </button>
          </div>
        )}

        {status === "timeout" && (
          <div data-testid="payment-timeout">
            <AlertCircle className="mx-auto h-12 w-12 text-amber-500" />
            <h1 className="mt-6 font-heading text-2xl font-extrabold text-ink sm:text-3xl">
              Still processing
            </h1>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-ink/65">
              Your payment is taking longer than usual to confirm. If money left your card, your
              booking is safe — call us on{" "}
              <a href={PHONE_TEL} className="font-semibold text-emerald-700">
                {PHONE_DISPLAY}
              </a>{" "}
              and quote your reference.
            </p>
            <button
              data-testid="payment-back-button-timeout"
              onClick={onDone}
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 font-heading text-sm font-bold text-ink transition-colors duration-300 hover:bg-ink hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Easyskip
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};
